import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { createCustomer, updateCustomer } from '../../redux/slices/customersSlice';
import { createDealer, updateDealer } from '../../redux/slices/dealersSlice';
import { createDevice, updateDevice } from '../../redux/slices/devicesSlice';
import { useLanguage } from '../../context/LanguageContext';
import Modal from './Modal';
import SearchCreateSelect from './SearchCreateSelect';

// Field layout per record type. `span` is the column span in a 6-column grid.
const KINDS = {
  customer: {
    create: createCustomer,
    update: updateCustomer,
    firstKey: 'name',
    newTitle: 'customers.createCustomer',
    editTitle: 'customers.editCustomer',
    fields: [
      { key: 'name', label: 'common.name', required: true, span: 6 },
      { key: 'email', label: 'customers.email', type: 'email', span: 3 },
      { key: 'tel', label: 'customers.phone', span: 3 },
      { key: 'street', label: 'customers.street', span: 4 },
      { key: 'house_number', label: 'customers.number', span: 2 },
      { key: 'zip', label: 'customers.zip', span: 2 },
      { key: 'city', label: 'customers.city', span: 4 },
    ],
  },
  dealer: {
    create: createDealer,
    update: updateDealer,
    firstKey: 'name',
    newTitle: 'dealers.createDealer',
    editTitle: 'dealers.editDealer',
    fields: [
      { key: 'name', label: 'common.name', required: true, span: 4 },
      { key: 'templateID', label: 'dealers.templateId', type: 'number', span: 2 },
      { key: 'nickname', label: 'dealers.nickname', span: 3 },
      { key: 'contact_person', label: 'dealers.contactPerson', span: 3 },
      { key: 'email', label: 'dealers.email', type: 'email', span: 3 },
      { key: 'tel', label: 'dealers.phone', span: 3 },
      { key: 'street', label: 'dealers.street', span: 6 },
      { key: 'zip', label: 'dealers.zip', span: 2 },
      { key: 'city', label: 'dealers.city', span: 4 },
    ],
    toPayload: (v) => ({ ...v, templateID: v.templateID === '' || v.templateID == null ? null : Number(v.templateID) }),
  },
  device: {
    create: createDevice,
    update: updateDevice,
    firstKey: 'serial_number',
    newTitle: 'devices.createDevice',
    editTitle: 'devices.editDevice',
    fields: [
      { key: 'serial_number', label: 'devices.serialNumber', required: true, span: 6 },
      { key: 'brand', label: 'devices.brand', span: 3 },
      { key: 'model', label: 'devices.model', span: 3 },
    ],
  },
};

const spanClass = { 2: 'col-span-2', 3: 'col-span-3', 4: 'col-span-4', 6: 'col-span-6' };

/**
 * Create / edit form for a customer, dealer or device, shown on top of
 * whatever modal is open. Rendered in a portal and without a <form>, so it
 * can sit inside another form (the repair ticket) without submitting it.
 * `onSaved(record)` gets the created/updated record.
 */
export const EntityFormModal = ({ kind, mode, initial, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const cfg = KINDS[kind];
  const [values, setValues] = useState({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const isOpen = !!mode;

  useEffect(() => {
    if (!isOpen) return;
    const v = {};
    cfg.fields.forEach((f) => {
      v[f.key] = initial?.[f.key] ?? '';
    });
    setValues(v);
    setError('');
  }, [isOpen, initial, cfg]);

  const save = async () => {
    const missing = cfg.fields.find((f) => f.required && !String(values[f.key] || '').trim());
    if (missing) {
      setError(`${t(missing.label)}: ${t('common.required')}`);
      return;
    }
    const payload = cfg.toPayload ? cfg.toPayload(values) : values;
    setSaving(true);
    setError('');
    try {
      const record = await dispatch(
        mode === 'edit' ? cfg.update({ id: initial.id, data: payload }) : cfg.create(payload),
      ).unwrap();
      onSaved(record);
    } catch (err) {
      setError(err?.error || err?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return createPortal(
    <Modal isOpen onClose={onClose} title={t(mode === 'edit' ? cfg.editTitle : cfg.newTitle)} size='md'>
      <div
        className='space-y-4'
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          }
        }}
      >
        {error && <div className='p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700'>{error}</div>}
        <div className='grid grid-cols-6 gap-4'>
          {cfg.fields.map((f, i) => (
            <div key={f.key} className={spanClass[f.span]}>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {t(f.label)}
                {f.required ? ' *' : ''}
              </label>
              <input
                type={f.type || 'text'}
                value={values[f.key] ?? ''}
                autoFocus={i === (initial?.[cfg.firstKey] ? 1 : 0)}
                onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                className='input-field'
              />
            </div>
          ))}
        </div>
        <div className='flex justify-end space-x-3 pt-2'>
          <button type='button' onClick={onClose} className='btn-secondary'>
            {t('common.cancel')}
          </button>
          <button type='button' onClick={save} disabled={saving} className='btn-primary'>
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </Modal>,
    document.body,
  );
};

/**
 * Read-only view of a customer, dealer or device (same fields as the form),
 * with a button to switch to editing.
 */
const EntityViewModal = ({ kind, record, onClose, onEdit }) => {
  const { t } = useLanguage();
  const cfg = KINDS[kind];
  if (!record) return null;
  return createPortal(
    <Modal isOpen onClose={onClose} title={record[cfg.firstKey] || t('common.view')} size='md'>
      <div className='space-y-4'>
        <div className='grid grid-cols-6 gap-4'>
          {cfg.fields.map((f) => (
            <div key={f.key} className={spanClass[f.span]}>
              <p className='text-sm text-gray-500'>{t(f.label)}</p>
              <p className='font-medium text-gray-900 break-words'>
                {record[f.key] === null || record[f.key] === undefined || record[f.key] === '' ? '-' : String(record[f.key])}
              </p>
            </div>
          ))}
        </div>
        <div className='flex justify-end space-x-3 pt-2'>
          <button type='button' onClick={onClose} className='btn-secondary'>
            {t('common.close')}
          </button>
          <button type='button' onClick={onEdit} className='btn-primary'>
            ✏️ {t('common.edit')}
          </button>
        </div>
      </div>
    </Modal>,
    document.body,
  );
};

/**
 * Searchable customer / dealer / device picker. Typing something that
 * doesn't exist opens the full form prefilled with it; the picked record
 * gets an Edit button.
 */
const EntityLookup = ({ kind, label, records, value, onChange, options, placeholder, extra }) => {
  const { t } = useLanguage();
  const cfg = KINDS[kind];
  const [form, setForm] = useState(null); // { mode: 'new' | 'edit', initial }
  const [viewing, setViewing] = useState(false);
  const selected = records.find((r) => r.id === value) || null;

  return (
    <>
      <label className='block text-sm font-medium text-gray-700 mb-2 flex items-center justify-between'>
        <span>{label}</span>
        <span className='flex items-center gap-3'>
          {extra}
          {selected ? (
            <>
              <button
                type='button'
                onClick={() => setViewing(true)}
                className='text-xs text-blue-600 hover:text-blue-800 font-normal'
              >
                👁 {t('common.view')}
              </button>
              <button
                type='button'
                onClick={() => setForm({ mode: 'edit', initial: selected })}
                className='text-xs text-blue-600 hover:text-blue-800 font-normal'
              >
                ✏️ {t('common.edit')}
              </button>
            </>
          ) : (
            <button
              type='button'
              onClick={() => setForm({ mode: 'new', initial: {} })}
              className='text-xs text-blue-600 hover:text-blue-800 font-normal'
            >
              {t('repairReport.newLabel')}
            </button>
          )}
        </span>
      </label>
      <SearchCreateSelect
        options={options}
        value={value}
        onChange={onChange}
        onCreate={(text) => {
          setForm({ mode: 'new', initial: { [cfg.firstKey]: text } });
          return null;
        }}
        placeholder={placeholder}
      />
      <EntityViewModal
        kind={kind}
        record={viewing ? selected : null}
        onClose={() => setViewing(false)}
        onEdit={() => {
          setViewing(false);
          setForm({ mode: 'edit', initial: selected });
        }}
      />
      <EntityFormModal
        kind={kind}
        mode={form?.mode}
        initial={form?.initial}
        onClose={() => setForm(null)}
        onSaved={(record) => {
          if (form.mode === 'new') onChange(record.id);
          setForm(null);
        }}
      />
    </>
  );
};

export default EntityLookup;
